import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Extends HTMLElement so parent components can still access refs.
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement | null} */
  #details = null;

  /** @type {HTMLElement | null} */
  #summary = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @returns {HTMLDetailsElement} */
  get details() {
    if (!this.#details) {
      const details = this.querySelector('details');

      if (!(details instanceof HTMLDetailsElement)) {
        throw new Error('AccordionCustom: details element not found');
      }

      this.#details = details;
    }

    return this.#details;
  }

  /** @returns {HTMLElement} */
  get summary() {
    if (!this.#summary) {
      const summary = this.details.querySelector('summary');

      if (!(summary instanceof HTMLElement)) {
        throw new Error('AccordionCustom: summary element not found');
      }

      this.#summary = summary;
    }

    return this.#summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.#handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#details = null;
    this.#summary = null;
  }

  /**
   * Handles click events on the summary.
   * @param {Event} event
   */
  #handleClick = (event) => {
    const isMobile = isMobileBreakpoint();

    if ((isMobile && this.#disableOnMobile) || (!isMobile && this.#disableOnDesktop)) {
      event.preventDefault();
    }
  };

  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state based on breakpoint attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keyboard events.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#closeWithEscape) return;

    event.preventDefault();
    this.details.open = false;
    this.summary.focus();
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}